import React, { useState, useEffect } from 'react';
import SwiperGallery from './SwiperGallery';
// Import lightbox library
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
// Import Swiper modules
import { Pagination } from 'swiper/modules';
// Import optional plugins
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import "yet-another-react-lightbox/plugins/thumbnails.css";

interface GalleryProps {
  language: string;
}

interface GalleryImage {
  src: string;
  alt: string;
}

const galleryImages: GalleryImage[] = [
  {
    src: "/images/Gallery/1.png",
    alt: "Restaurant interior with ambient lighting"
  },
  {
    src: "/images/Gallery/2.png",
    alt: "Fresh seafood platter"
  },
  {
    src: "/images/Gallery/3.png",
    alt: "Elegant table setting"
  },
  {
    src: "/images/Gallery/4.png",
    alt: "Signature dish with garnish"
  },
  {
    src: "/images/Gallery/5.png",
    alt: "Chef preparing food in kitchen"
  },
  {
    src: "/images/Gallery/6.png",
    alt: "Cocktail being prepared"
  },
  {
    src: "/images/Gallery/7.png",
    alt: "Outdoor dining area"
  },
  {
    src: "/images/Gallery/8.png",
    alt: "Dessert platter"
  },
  {
    src: "/images/Gallery/9.png",
    alt: "Private dining room"
  },
  {
    src: "/images/Gallery/10.png",
    alt: "Beach view dining"
  },
  {
    src: "/images/Gallery/11.png",
    alt: "Signature cocktails"
  },
  {
    src: "/images/Gallery/12.png",
    alt: "People enjoying the beach"
  }
];

export function Gallery({ language }: GalleryProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Create lightbox slides from gallery images
  const slides = galleryImages.map(image => ({
    src: image.src,
    alt: image.alt,
  }));

  const openImageModal = (image: GalleryImage, index: number) => {
    setCurrentIndex(index);
    setIsOpen(true);
  };

  return (
    <section id="gallery" className="gallery-section py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="section-title">
          <h2>{language === 'en' ? 'Our Gallery' : 'A Nossa Galeria'}</h2>
          <div className="title-separator"></div>
          <p className="section-subtitle">
            {language === 'en' 
              ? "A space crafted with heart and soul, made for those who celebrate life in every moment."
              : "Um espaço criado com carinho e alma, pensado para quem celebra a vida em cada momento."}
          </p>
        </div>
        
        {isMobile ? (
          // Mobile Swiper Gallery
          <>
            <SwiperGallery 
              images={galleryImages}
              onImageClick={openImageModal}
            />
            <div className="swipe-hint text-center text-sm text-gray-500 mt-4">
              <p>
                <i className="fas fa-hand-point-right mr-2"></i>
                {language === 'en' ? 'Swipe to see more photos' : 'Deslize para ver mais fotos'}
              </p>
            </div>
          </>
        ) : (
          // Desktop Gallery Grid
          <div className="gallery-masonry mt-12">
            {galleryImages.map((image, index) => (
              <div 
                key={`gallery-${index}`} 
                className="gallery-item-wrapper"
                onClick={() => openImageModal(image, index)}
              >
                <div className="gallery-item">
                  <img 
                    src={image.src} 
                    alt={image.alt} 
                    className="gallery-image"
                    loading="lazy" 
                  />
                  <div className="gallery-item-overlay">
                    <div className="gallery-item-icon">
                      <i className="fas fa-search-plus"></i>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Instagram Button */}
        <div className="instagram-button-container mt-10 mb-16 flex justify-center">
          <a 
            href="https://www.instagram.com/rest.nopatio" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="instagram-btn flex items-center gap-2 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] text-white px-6 py-3 rounded-full transition-all duration-300 hover:shadow-lg hover:-translate-y-1 font-bold"
          >
            <i className="fab fa-instagram text-xl"></i>
            <span>{language === 'en' ? 'See More on Instagram' : 'Ver Mais no Instagram'}</span>
          </a>
        </div>
      </div>
      
      {/* Yet Another React Lightbox */}
      <Lightbox
        open={isOpen}
        close={() => setIsOpen(false)}
        slides={slides}
        index={currentIndex}
        plugins={[Zoom, Thumbnails, Fullscreen]}
        carousel={{ finite: galleryImages.length <= 5 }}
        animation={{ swipe: 250 }}
        controller={{ closeOnBackdropClick: true }}
        thumbnails={{
          position: "bottom",
          width: 120,
          height: 80,
          gap: 10,
          vignette: true,
        }}
        styles={{
          container: { backgroundColor: "rgba(0, 0, 0, .9)" },
          thumbnailsContainer: { backgroundColor: "rgba(0, 0, 0, .8)" },
          thumbnail: { border: "2px solid #ffc800" },
          thumbnailsTrack: { padding: "10px 0" },
        }}
      />

      <style>{`
        /* Gallery Styles */
        .gallery-section {
          padding: 4rem 0;
          background-color: #fff;
        }

        .gallery-masonry {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }

        @media (min-width: 576px) {
          .gallery-masonry {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (min-width: 768px) {
          .gallery-masonry {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (min-width: 1200px) {
          .gallery-masonry {
            grid-template-columns: repeat(4, 1fr);
          }
        }

        .gallery-item-wrapper {
          cursor: pointer;
          overflow: hidden;
          border-radius: 8px;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
        }

        .gallery-item-wrapper:hover {
          transform: translateY(-5px);
        }

        .gallery-item {
          position: relative;
          overflow: hidden;
          width: 100%;
          height: 100%;
          aspect-ratio: 1;
        }

        .gallery-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .gallery-item-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.4);
          display: flex;
          justify-content: center;
          align-items: center;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .gallery-item:hover .gallery-item-overlay {
          opacity: 1;
        }

        .gallery-item:hover .gallery-image {
          transform: scale(1.1);
        }

        .gallery-item-icon {
          color: white;
          font-size: 1.5rem;
        }

        /* Image Modal Styles */
        .image-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.9);
          z-index: 1000;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 1rem;
        }

        .image-modal-content {
          position: relative;
          max-width: 100%;
          max-height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }

        .image-modal-img-container {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        .image-modal-img {
          max-width: 100%;
          max-height: 85vh;
          object-fit: contain;
        }

        .image-modal-counter {
          color: white;
          position: absolute;
          bottom: -25px;
          font-size: 0.9rem;
        }

        .image-modal-close {
          position: absolute;
          top: -40px;
          right: 0;
          background: transparent;
          border: none;
          color: white;
          font-size: 1.5rem;
          cursor: pointer;
          padding: 0.5rem;
          z-index: 1001;
        }

        .image-modal-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.2);
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.2rem;
          cursor: pointer;
          transition: background 0.3s ease;
          z-index: 1001;
        }

        .image-modal-prev {
          left: 20px;
        }

        .image-modal-next {
          right: 20px;
        }

        .image-modal-nav:hover {
          background: rgba(255, 255, 255, 0.4);
        }

        @media (max-width: 768px) {
          .image-modal-content {
            width: 100%;
          }
          .image-modal-nav {
            width: 35px;
            height: 35px;
            font-size: 1rem;
          }
          .image-modal-close {
            top: -30px;
            font-size: 1.2rem;
          }
        }

        /* Mobile Gallery Swiper Styles */
        .gallery-swiper {
          width: 100%;
          padding: 0 10px;
        }

        .gallery-swiper .swiper-slide {
          height: 250px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .gallery-swiper .gallery-item {
          width: 100%;
          height: 100%;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .gallery-swiper .gallery-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .gallery-swiper .swiper-pagination,
        .swiper-pagination-gallery {
          position: relative;
          bottom: 0 !important;
          margin-top: 15px;
          display: flex;
          justify-content: center;
        }

        .gallery-swiper .swiper-pagination-bullet,
        .swiper-pagination-gallery .swiper-pagination-bullet {
          width: 8px;
          height: 8px;
          transition: all 0.3s ease;
          opacity: 0.7;
          background: #ccc;
          margin: 0 5px;
        }

        .gallery-swiper .swiper-pagination-bullet-active,
        .swiper-pagination-gallery .swiper-pagination-bullet-active {
          background-color: #ffc800;
          transform: scale(1.3);
          opacity: 1;
        }

        @media (max-width: 768px) {
          .gallery-swiper {
            margin-bottom: 10px;
          }
          .gallery-swiper .swiper-slide {
            height: 200px;
          }
        }

        /* Swipe Hint */
        .swipe-hint {
          text-align: center;
          color: #555;
          margin: 15px 0 5px;
          font-size: 0.9rem;
          opacity: 0.9;
          animation: fadeInUp 1s ease forwards;
        }
        
        .swipe-hint i {
          color: #ffc800;
          animation: pulse 1.5s infinite;
          display: inline-block;
          margin-right: 6px;
        }
        
        @keyframes pulse {
          0% {
            opacity: 0.6;
            transform: scale(0.9);
          }
          50% {
            opacity: 1;
            transform: scale(1.1);
          }
          100% {
            opacity: 0.6;
            transform: scale(0.9);
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 0.9;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
} 